import type { Contact } from '@/api/queries/conversations';
import {
  createContactMutationOptions,
  deleteContactMutationOptions,
  updateContactMutationOptions,
} from '@/api/queries/conversations';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export function useContactManagement() {
  const { t } = useTranslation('conversations');
  const queryClient = useQueryClient();

  const createContactMutation = useMutation({
    ...createContactMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success(t('contactManagement.create.contactCreated'));
    },
    onError: error => {
      console.error('Failed to create contact:', error);
      toast.error(t('contactManagement.create.createFailed'));
    },
  });

  const updateContactMutation = useMutation({
    ...updateContactMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success(t('contactManagement.update.contactUpdated'));
    },
    onError: error => {
      console.error('Failed to update contact:', error);
      toast.error(t('contactManagement.update.updateFailed'));
    },
  });

  const deleteContactMutation = useMutation({
    ...deleteContactMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success(t('contactManagement.delete.contactDeleted'));
    },
    onError: error => {
      console.error('Failed to delete contact:', error);
      toast.error(t('contactManagement.delete.deleteFailed'));
    },
  });

  const createContact = (
    contactData: Omit<Contact, 'id' | 'owner_user_id' | 'created_at' | 'updated_at'>
  ) => {
    return createContactMutation.mutateAsync(contactData);
  };

  const updateContact = (id: string, contactData: Partial<Contact>) => {
    return updateContactMutation.mutateAsync({ id, contactData });
  };

  const deleteContact = (contactId: string) => {
    return deleteContactMutation.mutateAsync(contactId);
  };

  return {
    createContact,
    updateContact,
    deleteContact,
    isLoading:
      createContactMutation.isPending ||
      updateContactMutation.isPending ||
      deleteContactMutation.isPending,
  };
}
